import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

/*
 * MENÚ DE TIPOS DE MOVIMIENTO · COMPONENTE ÚNICO DEL NÚCLEO (RF-150, D-239,
 * D-244, D-248, RN-407 a RN-413, RNF-082).
 *
 * ESTE MENÚ NO SE ESCRIBIÓ DE NUEVO: se PROMOVIÓ. Vivía dentro del historial
 * (`ListaDeMovimientos.jsx:785-806`) y la puerta más usada —el botón flotante del
 * armazón— lo esquivaba e iba directo al gasto (CR-04). El catálogo de cuatro
 * opciones, su orden y los textos de las opciones SE CONSERVAN TAL CUAL: están
 * aprobados y este incremento no los reabre (S-09-05, RN-407).
 *
 * UNA SOLA PUERTA (D-248, RN-431): el «+ Capturar» de dentro del historial se
 * retiró. Lo invocan las dos caras del mismo botón del armazón —el flotante en el
 * teléfono y el enlace lateral en escritorio—.
 *
 * EL GASTO VA PRIMERO Y VISUALMENTE PRIMERO (D-244, RN-429). No hay toque corto /
 * toque largo y no hay atajo directo al gasto: una conducta, siempre la misma. El
 * toque extra en la captura de un gasto QUEDA ACEPTADO a cambio de eso (RN-409) y
 * no se reabre sin una decisión nueva.
 *
 * EL TEXTO DE AYUDA DE LA CUENTA NO SE PROMOVIÓ (RN-407): decía «la cuenta viene
 * preseleccionada con la última que usaste en este dispositivo» y D-247 lo vuelve
 * falso. No se reemplazó por otro: la cuenta vacía se explica sola con su opción
 * inicial «Elige la cuenta…» (RN-436, T-02 de CR-10).
 *
 * LA RUTA `/movimientos/captura-rapida` NO SE DA DE BAJA (RN-410, RN-430): sigue
 * respondiendo y sigue abriendo el gasto. Lo que cambia es qué hace el BOTÓN, no
 * qué hace la DIRECCIÓN.
 */

/** Catálogo cerrado y en su orden, con los textos de hoy (RN-407, RN-429). */
export const TIPOS_DE_CAPTURA = Object.freeze([
  { clave: 'GASTO', ruta: '/movimientos/gasto', etiqueta: 'Gasto', admiteCategoria: true },
  { clave: 'INGRESO', ruta: '/movimientos/ingreso', etiqueta: 'Ingreso', admiteCategoria: false },
  {
    clave: 'TRASPASO',
    ruta: '/movimientos/traspaso',
    etiqueta: 'Traspaso entre cuentas',
    detalle: 'incluye pago de tarjeta y disposición de efectivo',
    admiteCategoria: false,
  },
  {
    clave: 'DEVOLUCION',
    ruta: '/movimientos/devolucion',
    etiqueta: 'Devolución o reembolso',
    detalle: 'no es cancelar: es un movimiento nuevo',
    admiteCategoria: true,
  },
]);

export const TITULO_DEL_MENU = 'Qué quieres registrar';

/*
 * RNF-082 EN NÚMEROS. Van como estilo del componente y no en la hoja global a
 * propósito, igual que la cinta de chips del historial: así la disposición se
 * puede afirmar en prueba de componente al ancho de referencia de 320 px sin
 * depender de que se cargue una hoja de estilos.
 *
 *   · 48 px de alto por opción: más que el mínimo de 44 px.
 *   · El velo termina ARRIBA de la barra de destinos: el menú no tapa la barra de
 *     navegación y la barra sigue tocándose (y al cambiar de pantalla el menú se
 *     cierra, RN-412).
 *   · Ancho tope de 430 px, el mismo del marco, y `overflowWrap` para que las dos
 *     opciones con explicación se lean completas a 320 px sin desborde horizontal.
 */
const ALTO_DE_LA_BARRA = 'calc(56px + env(safe-area-inset-bottom, 0px))';

const ESTILO_VELO = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: ALTO_DE_LA_BARRA,
  background: 'rgba(0, 0, 0, .45)',
  zIndex: 8,
};

const ESTILO_HOJA = {
  position: 'fixed',
  left: '50%',
  transform: 'translateX(-50%)',
  bottom: `calc(${ALTO_DE_LA_BARRA} + 8px)`,
  width: '100%',
  maxWidth: 430,
  maxHeight: '70vh',
  overflowY: 'auto',
  zIndex: 9,
  padding: '0 10px',
  boxSizing: 'border-box',
};

const ESTILO_CAJA = {
  background: 'var(--card)',
  border: '1px solid var(--bor)',
  borderRadius: 14,
  boxShadow: '0 -4px 20px rgba(0, 0, 0, .28)',
  padding: 10,
};

const ESTILO_OPCION = {
  display: 'block',
  width: '100%',
  minHeight: 48,
  padding: '8px 12px',
  marginTop: 6,
  textAlign: 'left',
  overflowWrap: 'break-word',
};

/*
 * D-478 · CUÁNDO LA OPCIÓN REEMPLAZA EL PASO EN VEZ DE EMPUJARLO. Por omisión cada
 * opción EMPUJA un paso, que es la conducta de siempre al abrir el menú sobre un
 * listado. Pero hay una pantalla que tiene que CEDER su paso: el resumen de
 * guardado, que vive en la misma ruta que el formulario (RN-394). Si la captura que
 * abre el menú se apilara encima, el atrás del teléfono aterrizaría en un
 * formulario limpio del tipo anterior y haría falta un segundo toque para llegar al
 * listado. Con el reemplazo, el atrás deja en el destino de regreso original de un
 * solo toque, también al cambiar de tipo (CA-FIX-M01 paso 5).
 *
 * EL MENÚ NO DECIDE ESTO Y NO SABE QUÉ PANTALLA LO ABRIÓ: lo decide quien publica
 * el contexto, y viaja en lo que devuelve `construirRuta`. Así el núcleo no aprende
 * qué rutas son resúmenes de captura, que es la frontera que D-218 defiende.
 *
 * NO TOCA D-244: abrir o cerrar el menú sin elegir nada sigue sin navegar y sin
 * tocar la pila. Esto solo cambia CÓMO navega una opción ya elegida.
 */

/**
 * @param abierto        si el menú está desplegado.
 * @param alCerrar       lo llama el menú cuando debe cerrarse (RN-412).
 * @param construirRuta  (opcion) => ruta | { ruta, reemplazarElPaso }. Es donde cada
 *                       opción hereda el contexto de la pantalla desde la que se
 *                       abrió el menú (RN-411) y, con la forma de objeto, si debe
 *                       reemplazar el paso actual (D-478). Si no se pasa, cada
 *                       opción abre su ruta pelada y empuja, como siempre.
 */
export function MenuDeTiposDeMovimiento({ abierto, alCerrar, construirRuta = null }) {
  const ubicacion = useLocation();

  /* RN-412: se cierra con la tecla de escape. */
  useEffect(() => {
    if (!abierto) return undefined;
    const alTeclear = (evento) => { if (evento.key === 'Escape') alCerrar?.(); };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [abierto, alCerrar]);

  /* RN-412: se cierra al cambiar de pantalla. No queda abierto detrás de otra
     pantalla ni reaparece al volver. */
  useEffect(() => {
    if (abierto) alCerrar?.();
    // Depende SOLO de la ubicación: es el cambio de pantalla lo que cierra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ubicacion.pathname]);

  if (!abierto) return null;

  return (
    <>
      {/* RN-412: se cierra al tocar fuera. */}
      <div style={ESTILO_VELO} data-prueba="velo-del-menu-de-captura" onClick={() => alCerrar?.()} />

      <div
        style={ESTILO_HOJA}
        role="dialog"
        aria-modal="true"
        aria-label={TITULO_DEL_MENU}
        data-prueba="menu-de-captura"
      >
        <div className="card" style={ESTILO_CAJA}>
          <h4 style={{ margin: '2px 2px 4px', fontSize: 12 }}>{TITULO_DEL_MENU}</h4>

          {TIPOS_DE_CAPTURA.map((opcion, indice) => {
            /*
             * Dos formas admitidas, y la cadena pelada sigue valiendo: quien no
             * tenga nada que decir sobre la pila devuelve la ruta y ya (D-478).
             */
            const destino = construirRuta ? construirRuta(opcion) : opcion.ruta;
            const ruta = typeof destino === 'string' ? destino : destino.ruta;
            const reemplazarElPaso = typeof destino !== 'string' && destino.reemplazarElPaso === true;

            return (
              <Link
                key={opcion.clave}
                /* El gasto, primero y visualmente primero (RN-429). */
                className={`btn ${indice === 0 ? '' : 'sec '}ancho`}
                style={ESTILO_OPCION}
                data-prueba={`opcion-de-captura-${opcion.clave}`}
                to={ruta}
                replace={reemplazarElPaso}
                onClick={() => alCerrar?.()}
              >
                <b>{opcion.etiqueta}</b>
                {opcion.detalle && (
                  <>
                    <br />
                    <small>{opcion.detalle}</small>
                  </>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default MenuDeTiposDeMovimiento;
